import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Card, EmptyState, ErrorNote, PageHeader } from "../components/ui";

interface Overview {
  role: string;
  cards: Array<{ label: string; value: string; hint?: string }>;
}

/**
 * The landing screen for everyone.
 *
 * Every role used to arrive somewhere different — an admin at the reporting
 * dashboard, a marketer and a customer at a list with no context at all. One
 * scoped endpoint gives each of them their own figures, so signing in answers
 * "where do I stand" before it asks "what do you want to do".
 *
 * The shortcuts below are the two or three things that role actually came to
 * do; anything more is the navigation's job, not this screen's.
 */
const SHORTCUTS: Record<string, Array<{ to: string; label: string }>> = {
  admin: [
    { to: "/licenses", label: "Licences" },
    { to: "/orders", label: "Orders" },
    { to: "/catalogue", label: "Products & plans" },
  ],
  marketer: [
    { to: "/payment-links", label: "Create a payment link" },
    { to: "/orders", label: "My sales" },
    { to: "/commission", label: "Commission" },
  ],
  customer: [
    { to: "/licenses", label: "My licences" },
    { to: "/projects", label: "My projects" },
    { to: "/plans", label: "Order something" },
  ],
};

export function Overview() {
  const { user } = useAuth();

  const overview = useQuery({
    queryKey: ["overview"],
    queryFn: () => api<Overview>("/api/v1/admin/overview"),
  });

  if (overview.isPending) return <p className="text-sm text-dim">Loading…</p>;
  if (overview.isError) {
    return <ErrorNote>Could not load your overview.</ErrorNote>;
  }

  const { role, cards } = overview.data;
  const shortcuts = SHORTCUTS[role] ?? [];

  return (
    <div className="space-y-6">
      <PageHeader
        title={greeting(user?.email ?? null)}
        lede="Where things stand right now."
      />

      <Card>
        {cards.length === 0 ? (
          <EmptyState>Nothing to show yet.</EmptyState>
        ) : (
          <dl className="grid gap-px bg-hairline sm:grid-cols-2 lg:grid-cols-4">
            {cards.map((card) => (
              <div key={card.label} className="bg-surface px-4 py-4">
                <dt className="field-label">{card.label}</dt>
                <dd className="tabular text-2xl text-ink">{card.value}</dd>
                {card.hint !== undefined && (
                  <dd className="text-xs text-dim">{card.hint}</dd>
                )}
              </div>
            ))}
          </dl>
        )}
      </Card>

      {shortcuts.length > 0 && (
        <Card title="Jump to">
          <div className="flex flex-wrap gap-2 p-4">
            {shortcuts.map((shortcut) => (
              <Link
                key={shortcut.to}
                to={shortcut.to}
                className="rounded border border-hairline px-3 py-2 text-sm text-muted hover:border-accent hover:text-ink"
              >
                {shortcut.label}
              </Link>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}

/** The local part of the address is what a person recognises as themselves. */
function greeting(email: string | null): string {
  if (email === null) return "Overview";
  const name = email.split("@")[0] ?? email;
  return `Hello, ${name}`;
}
